import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { Guide, GuideCloseReason, GuideStep } from './guide.models';
import { formatDuration } from './guides.service';

const SWIPE_PX = 50;

/** The guide popup: image steps with arrows, swipe, dots and keyboard, or a single video. */
@Component({
  selector: 'mindbase-guide-viewer',
  standalone: true,
  imports: [TranslateModule],
  templateUrl: './guide-viewer.component.html',
  styleUrl: './guide-viewer.component.scss'
})
export class GuideViewerComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input({ required: true }) guide!: Guide;
  /** Step to open at, so an unfinished guide resumes where the user left off. */
  @Input() startIndex = 0;
  /** Closed early: Skip button (`skipped`) or ×, Escape, backdrop (`dismissed`). The guide stays unfinished. */
  @Output() readonly closed = new EventEmitter<GuideCloseReason>();
  /** Finish / "I've watched it". */
  @Output() readonly completed = new EventEmitter<void>();
  /** A different step became visible. */
  @Output() readonly stepViewed = new EventEmitter<number>();

  @ViewChild('nextBtn') private nextBtn?: ElementRef<HTMLButtonElement>;

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private opener: HTMLElement | null = null;
  private previousBodyOverflow = '';
  private swipeStartX: number | null = null;

  protected index = 0;
  protected imageFailed = false;
  protected videoEnded = false;
  protected videoFailed = false;
  protected readonly formatDuration = formatDuration;

  protected get steps(): GuideStep[] {
    return this.guide.steps ?? [];
  }

  protected get step(): GuideStep {
    return this.steps[this.index];
  }

  protected get isLast(): boolean {
    return this.index === this.steps.length - 1;
  }

  ngOnInit(): void {
    this.index = Math.min(Math.max(this.startIndex, 0), Math.max(this.steps.length - 1, 0));
    this.opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }

  ngAfterViewInit(): void {
    this.focusNext();
  }

  ngOnDestroy(): void {
    document.body.style.overflow = this.previousBodyOverflow;
    this.opener?.focus?.();
  }

  protected close(reason: GuideCloseReason = 'dismissed'): void {
    if (this.guide.allowSkip) this.closed.emit(reason);
  }

  protected next(): void {
    if (this.guide.type === 'images' && !this.isLast) {
      this.goTo(this.index + 1);
    } else {
      this.completed.emit();
    }
  }

  protected back(): void {
    if (this.index > 0) this.goTo(this.index - 1);
  }

  protected goTo(i: number): void {
    if (i === this.index) return;
    this.index = i;
    this.imageFailed = false;
    this.stepViewed.emit(i);
    this.focusNext();
  }

  protected onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) this.close();
  }

  protected onPointerDown(event: PointerEvent): void {
    this.swipeStartX = event.clientX;
  }

  protected onPointerUp(event: PointerEvent): void {
    if (this.swipeStartX === null) return;
    const dx = event.clientX - this.swipeStartX;
    this.swipeStartX = null;
    if (dx < -SWIPE_PX && !this.isLast) this.next();
    else if (dx > SWIPE_PX) this.back();
  }

  @HostListener('document:keydown', ['$event'])
  protected onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.close();
      return;
    }
    if (event.key === 'Tab') {
      this.trapFocus(event);
      return;
    }
    if (this.guide.type !== 'images' || document.activeElement?.tagName === 'VIDEO') return;
    if (event.key === 'ArrowRight' && !this.isLast) this.goTo(this.index + 1);
    if (event.key === 'ArrowLeft' && this.index > 0) this.goTo(this.index - 1);
  }

  private trapFocus(event: KeyboardEvent): void {
    const items = Array.from(
      this.host.nativeElement.querySelectorAll<HTMLElement>('button:not([disabled]), video[controls]')
    );
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /** Mirrors the design: the primary button takes focus after every step change. */
  private focusNext(): void {
    setTimeout(() => this.nextBtn?.nativeElement.focus({ preventScroll: true }));
  }
}
